import { useEffect, useState } from 'react';
import { Coach } from '@/features/coach';
import { getUser } from '@/services/mockData';
import { Canvas } from '../canvas/Canvas';
import { NicheDetail } from '../modules/niche/NicheDetail';
import { BrandDetail } from '../modules/brand/BrandDetail';
import { RoadmapDetail } from '../modules/roadmap/RoadmapDetail';
import { BuyerDetail } from '../modules/buyer/BuyerDetail';
import { ContentDetail } from '../modules/content/ContentDetail';
import { PortfolioDetail } from '../modules/portfolio/PortfolioDetail';
import { ValueDetail } from '../modules/value/ValueDetail';
import { AddedDetail } from '../modules/added/AddedDetail';
import { DeliveryView } from '../modules/delivery/DeliveryView';
import { OpportunitiesDetail } from '../modules/opportunities/OpportunitiesDetail';
import { ArticlesDetail } from '../modules/articles/ArticlesDetail';
import { ContactsDetail } from '../modules/contacts/ContactsDetail';
import { NurtureDetail } from '../modules/nurture/NurtureDetail';
import { Header } from './Header';
import { TweaksPanel } from './TweaksPanel';
import { useTabRouting } from '../../hooks/useTabRouting';

const DETAIL_BY_SLUG = {
  niche:         NicheDetail,
  brand:         BrandDetail,
  roadmap:       RoadmapDetail,
  buyer:         BuyerDetail,
  content:       ContentDetail,
  portfolio:     PortfolioDetail,
  value:         ValueDetail,
  added:         AddedDetail,
  opportunities: OpportunitiesDetail,
  articles:      ArticlesDetail,
  contacts:      ContactsDetail,
  nurture:       NurtureDetail,
};

// Top-level layout for The Portfolio Executive Companion workspace.
//   row 1 : 56px header
//   row 2 : 380px coach + 1fr canvas (with detail overlay layered on top)

function oppToClient(opp) {
  const contactParts = [opp.connect, opp.position].filter(Boolean);
  return {
    id: `cli_${opp.id}_${Date.now()}`,
    company: opp.firm || 'New Client',
    contact: contactParts.join(' · ') || '—',
    contact_url:   opp.connect_url   || '',
    contact_level: opp.connect_level || 'L1',
    plan: 'TBC',
    theme_focus: '',
    mood: 'new',
    started_at: new Date().toISOString().slice(0, 10),
    ends_at: null,
    end_label: 'Ongoing',
    note: opp.commentary || '',
    next_activity: opp.history?.[0]?.note || '—',
    maturity: { thm_01: 0, thm_02: 0, thm_03: 0, thm_04: 0, thm_05: 0, thm_06: 0 },
    actions: {},
    _origin_opp: opp,
  };
}

function splitContact(str = '') {
  const idx = str.indexOf(' · ');
  return idx >= 0 ? [str.slice(0, idx), str.slice(idx + 3)] : [str, ''];
}

function clientToOpp(client) {
  const [name, position] = splitContact(client.contact);
  return {
    id: `opp_${Date.now()}`,
    stage: 'qualify',
    firm: client.company,
    firm_url: '',
    viable: 'Possible',
    connect: name,
    connect_url:   client.contact_url   || '',
    position,
    connect_level: client.contact_level || 'L1',
    route_to_reach: '',
    route_url: '',
    route_level: 'L1',
    history: [],
    commentary: client.note && client.note !== '—' ? client.note : '',
  };
}

function clientToContact(client) {
  const [name, position] = splitContact(client.contact);
  return {
    id: `con_${Date.now()}`,
    firm: client.company,
    firm_url: '',
    viable: 'Possible',
    connect: name,
    connect_url:   client.contact_url   || '',
    position,
    connect_level: client.contact_level || 'L1',
    route_to_reach: '',
    route_url: '',
    route_level: 'L1',
    history: [],
    commentary: client.note && client.note !== '—' ? client.note : '',
  };
}

function clientToNurture(client) {
  const [name, position] = splitContact(client.contact);
  return {
    id: `nur_${Date.now()}`,
    firm: client.company,
    firm_url: '',
    viable: 'TBC',
    connect: name,
    connect_url:   client.contact_url   || '',
    position,
    connect_level: client.contact_level || 'L1',
    route_to_reach: '',
    route_url: '',
    route_level: 'L1',
    history: [],
    commentary: client.note && client.note !== '—' ? client.note : '',
  };
}

// Pipeline-internal converters — preserve all shared fields, swap id prefix only
function oppToContact(opp)    { const { stage, ...s } = opp; return { ...s, id: `con_${Date.now()}` }; }
function oppToNurture(opp)    { const { stage, ...s } = opp; return { ...s, id: `nur_${Date.now()}` }; }
function contactToOpp(c)      { return { ...c, id: `opp_${Date.now()}`, stage: 'link' }; }
function contactToNurture(c)  { return { ...c, id: `nur_${Date.now()}` }; }
function nurtureToOpp(c)      { return { ...c, id: `opp_${Date.now()}`, stage: 'link' }; }
function nurtureToContact(c)  { return { ...c, id: `con_${Date.now()}` }; }

export function Workspace() {
  const { tab, setTab, active, setActive, handleGoto, context } = useTabRouting();
  const [user, setUser] = useState(null);
  const [promotedClients, setPromotedClients] = useState([]);
  const [archivedClientIds, setArchivedClientIds] = useState(new Set());
  const [promotedOpps, setPromotedOpps] = useState([]);
  const [promotedContacts, setPromotedContacts] = useState([]);
  const [promotedNurture, setPromotedNurture] = useState([]);

  useEffect(() => {
    let cancelled = false;
    getUser().then((u) => { if (!cancelled) setUser(u); });
    return () => { cancelled = true; };
  }, []);

  function handlePromoteToClient(opp) {
    setPromotedClients((prev) => [...prev, oppToClient(opp)]);
    setTab('delivery');
  }

  function handleArchiveClient(clientId) {
    setArchivedClientIds((prev) => new Set([...prev, clientId]));
  }

  function handleDemoteToOpp(client) {
    setArchivedClientIds((prev) => new Set([...prev, client.id]));
    setPromotedOpps((prev) => [...prev, clientToOpp(client)]);
    handleGoto('opportunities');
  }

  function handleDemoteToContact(client) {
    setArchivedClientIds((prev) => new Set([...prev, client.id]));
    setPromotedContacts((prev) => [...prev, clientToContact(client)]);
    handleGoto('contacts');
  }

  function handleDemoteToNurture(client) {
    setArchivedClientIds((prev) => new Set([...prev, client.id]));
    setPromotedNurture((prev) => [...prev, clientToNurture(client)]);
    handleGoto('nurture');
  }

  function handleOppToContact(opp) {
    setPromotedContacts((prev) => [...prev, oppToContact(opp)]);
    handleGoto('contacts');
  }
  function handleOppToNurture(opp) {
    setPromotedNurture((prev) => [...prev, oppToNurture(opp)]);
    handleGoto('nurture');
  }
  function handleContactToOpp(contact) {
    setPromotedOpps((prev) => [...prev, contactToOpp(contact)]);
    handleGoto('opportunities');
  }
  function handleContactToNurture(contact) {
    setPromotedNurture((prev) => [...prev, contactToNurture(contact)]);
    handleGoto('nurture');
  }
  function handleNurtureToOpp(contact) {
    setPromotedOpps((prev) => [...prev, nurtureToOpp(contact)]);
    handleGoto('opportunities');
  }
  function handleNurtureToContact(contact) {
    setPromotedContacts((prev) => [...prev, nurtureToContact(contact)]);
    handleGoto('contacts');
  }

  return (
    <div className="grid h-screen grid-rows-[var(--hdr-h)_minmax(0,1fr)] min-h-0">
      <Header tab={tab} onTab={setTab} user={user} />

      <div className="grid grid-cols-[var(--coach-w)_minmax(0,1fr)] min-h-0">
        <Coach context={context} onGoto={handleGoto} />
        <div className="relative min-w-0 min-h-0 bg-paper border-l border-rule">
          {tab === 'delivery' ? (
            <DeliveryView
              promotedClients={promotedClients}
              archivedClientIds={archivedClientIds}
              onArchiveClient={handleArchiveClient}
              onDemoteToOpp={handleDemoteToOpp}
              onDemoteToContact={handleDemoteToContact}
              onDemoteToNurture={handleDemoteToNurture}
            />
          ) : (
            <>
              <Canvas tab={tab} activeHub={active} onOpenHub={setActive} />
              <DetailLayer
                active={active}
                onClose={() => setActive(null)}
                onPromoteToClient={handlePromoteToClient}
                initialOpps={promotedOpps}
                initialContacts={promotedContacts}
                initialNurture={promotedNurture}
                onOppToContact={handleOppToContact}
                onOppToNurture={handleOppToNurture}
                onContactToOpp={handleContactToOpp}
                onContactToNurture={handleContactToNurture}
                onNurtureToOpp={handleNurtureToOpp}
                onNurtureToContact={handleNurtureToContact}
              />
            </>
          )}
        </div>
      </div>
      <TweaksPanel />
    </div>
  );
}

// Renders the matching module detail panel for the currently active hub.
function DetailLayer({
  active, onClose, onPromoteToClient,
  initialOpps, initialContacts, initialNurture,
  onOppToContact, onOppToNurture,
  onContactToOpp, onContactToNurture,
  onNurtureToOpp, onNurtureToContact,
}) {
  if (!active) return null;
  const Detail = DETAIL_BY_SLUG[active];
  if (!Detail) return null;

  const extras = {
    opportunities: { initialOpps, onMoveToContact: onOppToContact, onMoveToNurture: onOppToNurture },
    contacts:      { initialContacts, onMoveToOpp: onContactToOpp, onMoveToNurture: onContactToNurture },
    nurture:       { initialNurture, onMoveToOpp: onNurtureToOpp, onMoveToContact: onNurtureToContact },
  }[active] ?? {};

  return <Detail onClose={onClose} onPromoteToClient={onPromoteToClient} {...extras} />;
}

export default Workspace;
